/**
 * Bulk Upload Modal for Client-Side Sources
 * Handles bulk file uploads from client-side folder sources using the artifact upload endpoint
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import { File, Clock, X, CheckCircle, AlertCircle, Upload, Folder } from 'lucide-react';
import { uploadArtifact } from '../../api/artifacts';
import { useQueryClient } from '@tanstack/react-query';

interface BulkUploadModalProps {
  projectId: string;
  sourceId: string;
  sourceName: string;
  sourceFiles?: Array<{
    filename: string;
    path?: string;
    file_size_bytes: number;
    artifact_type: string;
    artifact_id?: string | null;
    created_at?: string | null;
  }>;
  sourceMetadata?: {
    file_extensions?: string[];
    exclude_patterns?: string[];
    recursive?: boolean;
    client_side?: boolean;
  };
  onClose: () => void;
}

interface FileToUpload {
  file: File | null;
  path: string;
  size: number;
  extension: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress?: number;
  error?: string;
  artifactId?: string;
}

export function BulkUploadModal({
  projectId,
  sourceId,
  sourceName,
  sourceFiles = [],
  sourceMetadata,
  onClose,
}: BulkUploadModalProps) {
  const queryClient = useQueryClient();
  const [directoryHandle, setDirectoryHandle] = useState<FileSystemDirectoryHandle | null>(null);
  const [filesToUpload, setFilesToUpload] = useState<FileToUpload[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStats, setUploadStats] = useState({
    total: 0,
    uploaded: 0,
    failed: 0,
    skipped: 0,
  });
  const [batchSize] = useState(10); // Upload 10 files at a time
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [filesLoaded, setFilesLoaded] = useState(false);
  const [forceReupload, setForceReupload] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const cancelledRef = useRef(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  
  // Filter out files that are already uploaded (unless force re-upload is enabled)
  const pendingFiles = forceReupload ? sourceFiles : sourceFiles.filter(f => !f.artifact_id);

  const supportedExtensions = new Set([
    '.pdf', '.docx', '.doc', '.xlsx', '.xls', '.xlsm', '.csv', '.pptx', '.ppt',
    '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.tif',
    '.dwg', '.dxf', '.fgdb',
  ]);

  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }, []);

  // Initialize files from source data - only once
  useEffect(() => {
    if (pendingFiles.length > 0 && filesToUpload.length === 0) {
      const initialFiles: FileToUpload[] = pendingFiles.map(fileInfo => ({
        file: null as any, // Will be populated when folder is selected
        path: fileInfo.path || fileInfo.filename,
        size: fileInfo.file_size_bytes,
        extension: fileInfo.filename.substring(fileInfo.filename.lastIndexOf('.')).toLowerCase(),
        status: 'pending' as const,
      }));
      setFilesToUpload(initialFiles);
      setUploadStats(prev => ({ ...prev, total: pendingFiles.length }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFiles.length]);

  // Auto-start upload when files are loaded - removed, handled in handleSelectFolder

  const handleSelectFolder = async () => {
    try {
      setIsScanning(true);
      setFilesLoaded(false);
      // @ts-ignore - File System Access API
      const handle = await window.showDirectoryPicker({
        mode: 'read',
      });
      
      setDirectoryHandle(handle);
      const loadedFilesResult = await loadFilesFromHandle(handle);
      
      if (loadedFilesResult && loadedFilesResult.length > 0) {
        // Wait for state to update
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Use the loaded files directly - they already have File objects
        console.log(`[BULK UPLOAD] Starting upload for ${loadedFilesResult.length} loaded files`);
        handleUpload(loadedFilesResult);
      } else {
        alert('No files were loaded. Please ensure you selected the correct folder.');
      }
    } catch (err: any) {
      setIsScanning(false);
      setFilesLoaded(false);
      if (err.name !== 'AbortError') {
        console.error('Error selecting folder:', err);
        alert(`Failed to select folder: ${err.message}`);
      }
    }
  };

  const loadFilesFromHandle = async (handle: FileSystemDirectoryHandle): Promise<FileToUpload[]> => {
    try {
      console.log(`[BULK UPLOAD] Loading ${pendingFiles.length} files using exact paths from source metadata`);
      
      const loadedFilesForUpload: FileToUpload[] = [];
      const failedFiles: Array<{ path: string; filename: string; reason: string }> = [];

      // Helper to navigate to a file by its path
      const getFileByPath = async (
        dirHandle: FileSystemDirectoryHandle,
        pathParts: string[]
      ): Promise<File | null> => {
        try {
          if (pathParts.length === 0) return null;
          
          if (pathParts.length === 1) {
            // Last part is the filename
            const fileName = pathParts[0];
            const fileHandle = await dirHandle.getFileHandle(fileName);
            return await fileHandle.getFile();
          } else {
            // Navigate through directories
            const [dirName, ...rest] = pathParts;
            const subDirHandle = await dirHandle.getDirectoryHandle(dirName);
            return getFileByPath(subDirHandle, rest);
          }
        } catch (err: any) {
          // File or directory not found
          throw err;
        }
      };

      // Helper to try finding file by filename only (fallback)
      const findFileByName = async (
        dirHandle: FileSystemDirectoryHandle,
        filename: string,
        visited = new Set<string>()
      ): Promise<File | null> => {
        try {
          // Avoid infinite loops - use a simple depth limit instead
          if (visited.size > 10) return null; // Limit search depth
          
          // Check current directory
          try {
            const fileHandle = await dirHandle.getFileHandle(filename);
            return await fileHandle.getFile();
          } catch {
            // Not in current directory, try searching subdirectories (limited)
            // Note: Full recursive search is expensive, so we'll skip this for now
            // and rely on path-based matching
            return null;
          }
        } catch {
          return null;
        }
      };

      // Load each file using its exact path from source metadata
      for (const sourceFile of pendingFiles) {
        const filePath = sourceFile.path || sourceFile.filename;
        if (!filePath) {
          failedFiles.push({ path: filePath || '', filename: sourceFile.filename, reason: 'No path or filename' });
          continue;
        }
        
        // Split path into parts (handle both / and \ separators)
        const pathParts = filePath.split(/[/\\]/).filter(p => p);
        
        try {
          let file = await getFileByPath(handle, pathParts);
          
          // If path-based lookup failed, try finding by filename only
          if (!file && sourceFile.filename) {
            console.log(`[BULK UPLOAD] Path lookup failed for ${filePath}, trying filename search: ${sourceFile.filename}`);
            file = await findFileByName(handle, sourceFile.filename);
            if (file) {
              console.log(`[BULK UPLOAD] Found file by filename search: ${sourceFile.filename}`);
            }
          }
          
          if (file) {
            const extension = sourceFile.filename.substring(sourceFile.filename.lastIndexOf('.')).toLowerCase();
            loadedFilesForUpload.push({
              file,
              path: filePath,
              size: sourceFile.file_size_bytes,
              extension,
              status: 'pending',
            });
          } else {
            failedFiles.push({ path: filePath, filename: sourceFile.filename, reason: 'File not found' });
            console.warn(`[BULK UPLOAD] File not found at path: ${filePath} (filename: ${sourceFile.filename})`);
          }
        } catch (err: any) {
          failedFiles.push({ path: filePath, filename: sourceFile.filename, reason: err.message || 'Unknown error' });
          console.error(`[BULK UPLOAD] Error loading file at ${filePath}:`, err);
        }
      }

      // Log summary of failed files
      if (failedFiles.length > 0) {
        console.warn(`[BULK UPLOAD] Failed to load ${failedFiles.length} files:`);
        failedFiles.slice(0, 10).forEach(f => {
          console.warn(`  - ${f.filename} (path: ${f.path}): ${f.reason}`);
        });
        if (failedFiles.length > 10) {
          console.warn(`  ... and ${failedFiles.length - 10} more`);
        }
      }

      // Update state with all loaded files
      if (loadedFilesForUpload.length > 0) {
        setFilesToUpload(prev => {
          const updated = [...prev];
          let updateCount = 0;
          
          loadedFilesForUpload.forEach(loadedFile => {
            const index = updated.findIndex(f => f.path === loadedFile.path);
            if (index !== -1) {
              updated[index] = {
                ...updated[index],
                file: loadedFile.file,
              };
              updateCount++;
            }
          });
          
          console.log(`[BULK UPLOAD] Updated ${updateCount} files with File objects in state`);
          return updated;
        });
      }

      console.log(`[BULK UPLOAD] Successfully loaded ${loadedFilesForUpload.length} of ${pendingFiles.length} files`);
      setFilesLoaded(true);
      return loadedFilesForUpload;
    } finally {
      setIsScanning(false);
    }
  };

  const handleUpload = async (filesOverride?: FileToUpload[]) => {
    // Use override if provided, otherwise use state
    const filesToProcess = filesOverride || filesToUpload;
    
    if (filesToProcess.length === 0) {
      console.warn('[BULK UPLOAD] No files to process');
      return;
    }

    // Check if we have File objects loaded
    const filesWithObjects = filesToProcess.filter(f => f.file !== null);
    if (filesWithObjects.length === 0) {
      console.warn('[BULK UPLOAD] No files with File objects loaded');
      // Try one more time with fresh state
      await new Promise(resolve => setTimeout(resolve, 500));
      const freshFiles = filesToUpload.filter(f => f.file !== null && f.status === 'pending');
      if (freshFiles.length === 0) {
        alert('Please select the folder first to load file data.');
        return;
      }
      return handleUpload(freshFiles);
    }

    if (isUploading) {
      console.warn('[BULK UPLOAD] Upload already in progress');
      return;
    }

    setIsUploading(true);
    setCurrentBatch(0);
    setCancelled(false); // Reset cancel flag when starting new upload
    cancelledRef.current = false; // Reset ref as well

    const filesReadyToUpload = filesToProcess.filter(f => f.status === 'pending' && f.file !== null);
    console.log(`[BULK UPLOAD] Starting upload for ${filesReadyToUpload.length} files`);
    
    if (filesReadyToUpload.length === 0) {
      console.warn('[BULK UPLOAD] No files ready to upload');
      setIsUploading(false);
      return;
    }

    const calculatedTotalBatches = Math.ceil(filesReadyToUpload.length / batchSize);
    setTotalBatches(calculatedTotalBatches);
    setCurrentBatch(1);

    for (let batchIdx = 0; batchIdx < calculatedTotalBatches; batchIdx++) {
      // Check if upload was cancelled before processing next batch
      if (cancelledRef.current) {
        console.log('[BULK UPLOAD] Upload cancelled by user');
        setIsUploading(false);
        break;
      }
      const batchNumber = batchIdx + 1;
      setCurrentBatch(batchNumber);
      const batch = filesReadyToUpload.slice(batchIdx * batchSize, (batchIdx + 1) * batchSize);
      
      // Upload files in parallel within batch
      const uploadPromises = batch.map(async (fileInfo, idx) => {
        const fileIndex = filesToUpload.findIndex(f => f.path === fileInfo.path);
        if (fileIndex === -1) return;

        setFilesToUpload(prev => {
          const updated = [...prev];
          updated[fileIndex] = { ...updated[fileIndex], status: 'uploading', progress: 0 };
          return updated;
        });

        if (!fileInfo.file) {
          setFilesToUpload(prev => {
            const updated = [...prev];
            updated[fileIndex] = {
              ...updated[fileIndex],
              status: 'error',
              error: 'File not loaded',
            };
            return updated;
          });
          setUploadStats(prev => ({ ...prev, failed: prev.failed + 1 }));
          return;
        }

        try {
          const artifact = await uploadArtifact(
            projectId,
            fileInfo.file,
            (progress) => {
              setFilesToUpload(prev => {
                const updated = [...prev];
                if (updated[fileIndex]) {
                  updated[fileIndex] = { ...updated[fileIndex], progress };
                }
                return updated;
              });
            },
            sourceId,
            'general_reference', // Default, can be enhanced with classification logic
            forceReupload
          );

          setFilesToUpload(prev => {
            const updated = [...prev];
            updated[fileIndex] = {
              ...updated[fileIndex],
              status: 'success',
              progress: 100,
              artifactId: artifact.id,
            };
            return updated;
          });

          setUploadStats(prev => ({ ...prev, uploaded: prev.uploaded + 1 }));
        } catch (err: any) {
          const errorMsg = err.response?.data?.detail || err.message || 'Upload failed';
          setFilesToUpload(prev => {
            const updated = [...prev];
            updated[fileIndex] = {
              ...updated[fileIndex],
              status: 'error',
              error: errorMsg,
            };
            return updated;
          });
          setUploadStats(prev => ({ ...prev, failed: prev.failed + 1 }));
        }
      });

      await Promise.all(uploadPromises);
      
      // Check again after batch completes
      if (cancelledRef.current) {
        console.log('[BULK UPLOAD] Upload cancelled after batch completion');
        setIsUploading(false);
        break;
      }
      
      // Small delay between batches to avoid overwhelming the server
      if (batchIdx < totalBatches - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    setIsUploading(false);
    
    if (cancelledRef.current) {
      console.log('[BULK UPLOAD] Upload was cancelled');
      // Still refresh to show progress made so far
      queryClient.invalidateQueries({ queryKey: ['sources', projectId] });
      return;
    }
    
    // Mark upload as successful
    setUploadSuccess(true);
    
    // Refresh sources after upload completes - multiple refreshes to ensure backend updates are reflected
    console.log('[BULK UPLOAD] Upload complete, refreshing source data...');
    
    // Immediate invalidation
    queryClient.invalidateQueries({ queryKey: ['sources', projectId] });
    queryClient.invalidateQueries({ queryKey: ['artifacts', projectId] });
    
    // Force refetch immediately
    await queryClient.refetchQueries({ queryKey: ['sources', projectId] });
    
    // Refresh again after delays to catch backend metadata updates
    setTimeout(async () => {
      await queryClient.refetchQueries({ queryKey: ['sources', projectId] });
    }, 1000);
    
    setTimeout(async () => {
      await queryClient.refetchQueries({ queryKey: ['sources', projectId] });
    }, 3000);
    
    // Final refresh after backend has processed all updates
    setTimeout(async () => {
      await queryClient.refetchQueries({ queryKey: ['sources', projectId] });
    }, 5000);
    
    // Auto-close modal after 5 seconds
    setTimeout(() => {
      onClose();
    }, 5000);
  };

  const handleRemoveFile = (index: number) => {
    setFilesToUpload(prev => {
      const updated = prev.filter((_, i) => i !== index);
      setUploadStats(prev => ({ ...prev, total: updated.length }));
      return updated;
    });
  };

  const successCount = filesToUpload.filter(f => f.status === 'success').length;
  const errorCount = filesToUpload.filter(f => f.status === 'error').length;
  const pendingCount = filesToUpload.filter(f => f.status === 'pending').length;
  const uploadingCount = filesToUpload.filter(f => f.status === 'uploading').length;

  return (
    <div className="fixed inset-0 -top-[30px] bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden bg-white dark:bg-slate-800 dark:border-slate-700 flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Bulk Upload Files</h3>
              <p className="text-sm text-gray-500 mt-1">Source: {sourceName}</p>
            </div>
            {!uploadSuccess && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
                disabled={isUploading}
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Success State */}
          {uploadSuccess && (
            <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 mb-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-green-900 mb-2">
                    Upload Complete!
                  </h3>
                  <p className="text-sm text-green-700 mb-3">
                    Successfully uploaded {successCount} file{successCount !== 1 ? 's' : ''} to the source.
                    {errorCount > 0 && (
                      <span className="block mt-1 text-yellow-700">
                        {errorCount} file{errorCount !== 1 ? 's' : ''} failed to upload.
                      </span>
                    )}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-green-600">
                    <Clock className="h-4 w-4" />
                    <span>This modal will close automatically in a few seconds...</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {pendingFiles.length === 0 && !uploadSuccess && (
            <div className="text-center py-12">
              <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">All Files Uploaded</h4>
              <p className="text-gray-600 mb-6">
                All files from this source have already been uploaded.
              </p>
            </div>
          )}

          {pendingFiles.length > 0 && !directoryHandle && !isUploading && !uploadSuccess && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <Upload className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-blue-900 mb-1">Ready to Upload</h4>
                  <p className="text-sm text-blue-800 mb-3">
                    This source has {pendingFiles.length} file{pendingFiles.length !== 1 ? 's' : ''} ready to upload.
                    Click the button below to select the folder and automatically start batch upload.
                  </p>
                  <div className="flex items-center gap-3 mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={forceReupload}
                        onChange={(e) => setForceReupload(e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-blue-800">
                        Force re-upload (bypass duplicate detection)
                      </span>
                    </label>
                  </div>
                  <Button onClick={handleSelectFolder} disabled={isScanning} size="sm">
                    <Folder className="h-4 w-4 mr-2" />
                    {isScanning ? 'Loading Files...' : 'Start Upload'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {isScanning && (
            <div className="text-center py-12">
              <Clock className="h-16 w-16 text-blue-400 mx-auto mb-4 animate-spin" />
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Scanning Folder...</h4>
              <p className="text-gray-600">Please wait while we scan for files...</p>
            </div>
          )}

          {pendingFiles.length > 0 && filesToUpload.length > 0 && (
            <>
              {/* Upload Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="text-sm text-blue-600 mb-1">Total Files</div>
                  <div className="text-2xl font-bold text-blue-900">{uploadStats.total}</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="text-sm text-green-600 mb-1">Uploaded</div>
                  <div className="text-2xl font-bold text-green-900">{successCount}</div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="text-sm text-yellow-600 mb-1">Pending</div>
                  <div className="text-2xl font-bold text-yellow-900">{pendingCount}</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="text-sm text-red-600 mb-1">Failed</div>
                  <div className="text-2xl font-bold text-red-900">{errorCount}</div>
                </div>
              </div>

              {/* Upload Progress */}
              {isUploading && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-yellow-600 animate-spin" />
                      <span className="text-sm font-semibold text-yellow-900">
                        Uploading Batch {currentBatch} of {totalBatches || Math.ceil(pendingCount / batchSize)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-yellow-700">
                        {uploadingCount} uploading, {successCount} completed
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setCancelled(true);
                          cancelledRef.current = true;
                          console.log('[BULK UPLOAD] Cancel requested - will stop after current batch');
                        }}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-yellow-500 h-2 rounded-full transition-all"
                      style={{ width: `${(successCount / uploadStats.total) * 100}%` }}
                    />
                  </div>
                  {cancelled && (
                    <div className="mt-2 text-sm text-orange-600 font-medium">
                      Cancellation requested - will stop after current batch completes...
                    </div>
                  )}
                </div>
              )}

              {/* Files List */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-700">
                    Files ({filesToUpload.length})
                  </h4>
                  {!isUploading && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleSelectFolder}
                    >
                      Rescan Folder
                    </Button>
                  )}
                </div>
                <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                  <div className="divide-y divide-gray-200">
                    {filesToUpload.map((fileInfo, idx) => {
                    const sourceFile = pendingFiles.find(f => 
                      (f.path || f.filename) === fileInfo.path
                    );
                    const fileName = fileInfo.file?.name || sourceFile?.filename || fileInfo.path.split('/').pop() || 'Unknown';
                    const isReady = fileInfo.file !== null;
                    
                    return (
                      <div
                        key={idx}
                        className={`flex items-center justify-between p-3 hover:bg-gray-50 transition-colors ${
                          fileInfo.status === 'error' ? 'bg-red-50' :
                          fileInfo.status === 'success' ? 'bg-green-50' :
                          fileInfo.status === 'uploading' ? 'bg-yellow-50' :
                          !isReady ? 'bg-gray-50 opacity-75' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {fileInfo.status === 'success' ? (
                            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                          ) : fileInfo.status === 'error' ? (
                            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                          ) : fileInfo.status === 'uploading' ? (
                            <Clock className="h-5 w-5 text-yellow-600 animate-spin flex-shrink-0" />
                          ) : !isReady ? (
                            <Clock className="h-5 w-5 text-gray-400 flex-shrink-0" />
                          ) : (
                            <File className="h-5 w-5 text-gray-400 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span
                                className="text-sm text-gray-900 dark:text-gray-100 truncate font-medium"
                                title={fileInfo.path}
                              >
                                {fileName}
                              </span>
                              <Badge variant="default" className="text-xs">
                                {fileInfo.extension}
                              </Badge>
                              {!isReady && (
                                <Badge variant="warning" className="text-xs">
                                  Not Loaded
                                </Badge>
                              )}
                              {fileInfo.status === 'success' && (
                                <Badge variant="success" className="text-xs">
                                  Uploaded
                                </Badge>
                              )}
                              {fileInfo.status === 'error' && (
                                <Badge variant="error" className="text-xs">
                                  Failed
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 mt-1 truncate">
                              {fileInfo.path}
                            </div>
                            {fileInfo.error && (
                              <div className="text-xs text-red-600 mt-1">{fileInfo.error}</div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                          <span className="text-sm font-medium text-gray-700">
                            {formatFileSize(fileInfo.size)}
                          </span>
                          {fileInfo.status === 'uploading' && fileInfo.progress !== undefined && (
                            <div className="w-24">
                              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-blue-500 transition-all"
                                  style={{ width: `${fileInfo.progress}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex gap-2 justify-end">
            {!uploadSuccess && (
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isUploading}
              >
                {isUploading ? 'Uploading...' : 'Close'}
              </Button>
            )}
            {filesToUpload.length > 0 && pendingCount > 0 && directoryHandle && !isUploading && !uploadSuccess && (
              <Button
                onClick={() => handleUpload()}
                disabled={isUploading || pendingCount === 0}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload {pendingCount} File{pendingCount !== 1 ? 's' : ''}
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

